import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Mail, Loader2, ExternalLink, Copy, Check, AlertTriangle } from 'lucide-react';
import DhiLogo from './DhiLogo';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('adminToken')}`, 'Content-Type': 'application/json' });
const scoreColor = (s) => (s < 40 ? '#DC2626' : s < 70 ? '#D97706' : '#22c55e');
const gradeColor = (g) => ({ A: '#22c55e', B: '#22c55e', C: '#D97706', D: '#DC2626', F: '#DC2626' }[g] || '#DC2626');
const fmtNum = (n) => { try { return Number(n).toLocaleString(); } catch { return n; } };
const fmtDate = (d) => { try { return new Date(d).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }); } catch { return '-'; } };

const PLATFORM_LOGOS = {
  chatgpt: 'https://cdn.prod.website-files.com/686e2126992edd7b040ec218/69ccedef9ce81893c1ef2c17_chatgpt-icon.webp',
  gemini: 'https://cdn.simpleicons.org/googlegemini/0A0F2E',
  perplexity: 'https://cdn.simpleicons.org/perplexity/0A0F2E',
  claude: 'https://cdn.simpleicons.org/claude/0A0F2E',
};
const PLATFORM_LABELS = { chatgpt: 'ChatGPT', gemini: 'Gemini', perplexity: 'Perplexity', claude: 'Claude' };
// Updated 2026-06-09 to match the redesigned scoring (PR #17-20). Legacy
// `pagespeed` + `onpage` keys preserved here so older audit payloads (before
// page_performance landed) still render label-correctly in the admin panel.
const WORKER_NAMES = {
  technical: 'AI Accessibility',
  page_performance: 'Page Performance',
  site_architecture: 'Site Architecture',
  pagespeed: 'Load Performance (legacy)',
  onpage: 'Core Web Vitals (legacy)',
  schema: 'Structured Data',
  content: 'Content Quality',
  authority: 'Market Authority',
  performance: 'Performance (raw)',
  geo: 'Entity & Local Relevance',
  crawl: 'Crawl Health',
  meta: 'Meta & Headings',
  perf: 'Performance',
};
const DIAG_KEYS = ['brand_recognition', 'competitive_share', 'content_depth', 'technical_readiness', 'trust_signals'];
const DIAG_LABELS = { brand_recognition: 'Brand Recognition', competitive_share: 'Competitive Share', content_depth: 'Content Depth', technical_readiness: 'Technical Readiness', trust_signals: 'Trust Signals' };
const PRIORITY_COLORS = { Critical: '#DC2626', High: '#D97706', Medium: 'rgba(10,10,26,0.5)', Low: 'rgba(10,10,26,0.3)' };

export default function AdminAuditDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [audit, setAudit] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('overview');
  const [resending, setResending] = useState(false);
  const [resendMsg, setResendMsg] = useState('');
  const [history, setHistory] = useState(null);
  const [historyLoading, setHistoryLoading] = useState(false);

  useEffect(() => {
    const token = localStorage.getItem('adminToken');
    if (!token) { navigate('/admin/login'); return; }
    fetch(`${API_URL}/api/admin/audits/${id}`, { headers: authHeaders() })
      .then(r => { if (!r.ok) throw new Error(); return r.json(); })
      .then(d => setAudit(d.data))
      .catch(() => navigate('/admin'))
      .finally(() => setLoading(false));
  }, [id, navigate]);

  // Lazy-load history the first time the user opens the History tab —
  // avoids the extra DB query on every audit-detail view.
  useEffect(() => {
    if (tab !== 'history' || history !== null || historyLoading) return;
    setHistoryLoading(true);
    fetch(`${API_URL}/api/admin/audits/${id}/history`, { headers: authHeaders() })
      .then(r => r.ok ? r.json() : Promise.reject(new Error('Failed to load history')))
      .then(d => setHistory(d.data))
      .catch(() => setHistory({ runs: [], error: true }))
      .finally(() => setHistoryLoading(false));
  }, [tab, id, history, historyLoading]);

  const handleResend = async () => {
    setResending(true); setResendMsg('');
    try {
      const r = await fetch(`${API_URL}/api/admin/audits/${id}/resend-email`, { method: 'POST', headers: authHeaders() });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || 'Failed');
      setResendMsg(`Sent to ${d.sent_to}`);
    } catch (e) { setResendMsg(`Error: ${e.message}`); }
    finally { setResending(false); }
  };

  if (loading) return <div style={{ minHeight: '100vh', background: '#F4F5F7', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><Loader2 size={32} className="animate-spin" color="#2D4DE0" /></div>;
  if (!audit) return null;

  const fr = audit.full_report || {};
  const ps = fr.platform_scores || audit.platform_scores || {};
  const findings = fr.findings || [];
  const workers = fr.worker_details || {};
  const engines = fr.engine_data || {};
  const card = { background: '#fff', borderRadius: 12, border: '1px solid rgba(10,10,26,0.08)', padding: '18px 20px' };
  const ghostBtn = { padding: '7px 14px', border: '1px solid rgba(10,10,26,0.14)', borderRadius: 8, background: '#fff', color: 'rgba(10,10,26,0.52)', fontSize: 12, fontWeight: 600, cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 6, fontFamily: "'DM Sans', sans-serif" };

  const tabs = [
    { key: 'overview', label: 'AI Overview' },
    { key: 'engines', label: 'Revenue Engines' },
    { key: 'workers', label: 'Site Audit' },
    { key: 'action', label: 'Action Plan' },
    { key: 'history', label: 'History' },
    { key: 'raw', label: 'Raw Data' },
    { key: 'logs', label: 'Logs' },
    { key: 'email', label: 'Email Log' },
    { key: 'pipeline', label: 'Pipeline' },
  ];

  return (
    <div style={{ minHeight: '100vh', background: '#F4F5F7', fontFamily: "'DM Sans', sans-serif" }} data-testid="admin-audit-detail">
      {/* Header */}
      <div style={{ background: '#0A0F2E', padding: '16px 28px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
          <DhiLogo variant="white" height={22} />
          <div>
            <div style={{ fontFamily: "'Outfit', sans-serif", fontWeight: 700, fontSize: 15, color: '#fff' }}>Audit Detail</div>
            <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.4)', fontFamily: 'monospace' }}>{id}</div>
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button onClick={() => navigate('/admin')} style={{ ...ghostBtn, borderColor: 'rgba(255,255,255,0.15)', color: 'rgba(255,255,255,0.6)', background: 'transparent' }} data-testid="btn-back"><ArrowLeft size={14} /> Back</button>
          <button onClick={handleResend} disabled={resending} style={{ padding: '7px 16px', border: 'none', borderRadius: 8, background: '#2D4DE0', color: '#fff', fontSize: 12, fontWeight: 700, cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 6, fontFamily: "'DM Sans', sans-serif" }} data-testid="btn-resend-email">
            {resending ? <Loader2 size={14} className="animate-spin" /> : <Mail size={14} />}
            {resending ? 'Sending...' : 'Resend Email'}
          </button>
        </div>
      </div>
      {resendMsg && <div style={{ padding: '8px 28px', background: resendMsg.startsWith('Error') ? '#FEF2F2' : '#ECFDF5', fontSize: 13, color: resendMsg.startsWith('Error') ? '#DC2626' : '#22c55e' }} data-testid="resend-msg">{resendMsg}</div>}

      <div style={{ maxWidth: 1200, margin: '0 auto', padding: '24px 20px' }}>
        {/* Summary Card */}
        <div style={{ ...card, marginBottom: 18, display: 'flex', gap: 20 }} data-testid="summary-card">
          <div style={{ flex: 1 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
              <span style={{ display: 'inline-block', padding: '4px 12px', borderRadius: 8, fontSize: 14, fontWeight: 800, color: '#fff', background: gradeColor(audit.grade) }}>Grade {audit.grade || '-'}</span>
              <span style={{ display: 'inline-block', padding: '4px 12px', borderRadius: 8, fontSize: 14, fontWeight: 700, color: '#fff', background: scoreColor(audit.final_score || 0) }}>{audit.final_score || 0}/100</span>
            </div>
            <div style={{ fontSize: 18, fontWeight: 800, color: '#0A0A1A', fontFamily: "'Outfit', sans-serif", marginBottom: 4 }}>{audit.hotel_name}</div>
            <div style={{ fontSize: 13, color: 'rgba(10,10,26,0.5)', marginBottom: 8 }}>
              {[audit.city, audit.country].filter(Boolean).join(', ')}
              {audit.url && <> &middot; <a href={audit.url} target="_blank" rel="noopener noreferrer" style={{ color: '#2D4DE0', textDecoration: 'none' }}>{audit.url} <ExternalLink size={11} style={{ verticalAlign: 'middle' }} /></a></>}
            </div>
            <div style={{ fontSize: 13, color: 'rgba(10,10,26,0.5)' }}>
              {audit.email && <>Email: <strong>{audit.email}</strong></>}
            </div>
            {audit.grade_label && <div style={{ fontSize: 11, color: 'rgba(10,10,26,0.4)', marginTop: 6, textAlign: 'center' }}>{
              ({ A: 'Strong AI visibility across all platforms', B: 'Good visibility — trust signals need strengthening', C: 'Partial visibility — structured data holding you back', D: 'Weak AI visibility — schema and content gaps critical', E: 'Largely invisible to AI — infrastructure is the gap', F: 'Largely invisible to AI — infrastructure is the gap' })[audit.grade] || 'AI visibility audit complete'
            }</div>}
          </div>
          <div style={{ width: 260, background: '#F8F9FF', borderRadius: 10, padding: '14px 18px', fontSize: 12 }}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px 14px' }}>
              <div><span style={{ color: 'rgba(10,10,26,0.4)' }}>Rooms:</span> <strong>{audit.rooms}</strong></div>
              <div><span style={{ color: 'rgba(10,10,26,0.4)' }}>ADR:</span> <strong>{audit.currency} {fmtNum(audit.adr || 0)}</strong></div>
              <div><span style={{ color: 'rgba(10,10,26,0.4)' }}>Currency:</span> <strong>{audit.currency}</strong></div>
              <div><span style={{ color: 'rgba(10,10,26,0.4)' }}>LOS:</span> <strong>{audit.los}</strong></div>
              <div style={{ gridColumn: '1 / -1' }}><span style={{ color: 'rgba(10,10,26,0.4)' }}>Audited:</span> <strong>{fmtDate(audit.audited_at || audit.created_at)}</strong></div>
              {fr.archetype && <div style={{ gridColumn: '1 / -1' }}><span style={{ color: 'rgba(10,10,26,0.4)' }}>Archetype:</span> <strong>{fr.archetype}</strong></div>}
              {fr.active_modules?.length > 0 && (
                <div style={{ gridColumn: '1 / -1', display: 'flex', flexWrap: 'wrap', gap: 4, marginTop: 4 }}>
                  {fr.active_modules.map((m, i) => <span key={i} style={{ padding: '2px 8px', borderRadius: 4, background: '#EEF2FF', fontSize: 10, fontWeight: 600, color: '#2D4DE0' }}>{m}</span>)}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div style={{ display: 'flex', gap: 4, marginBottom: 18, background: '#fff', borderRadius: 10, border: '1px solid rgba(10,10,26,0.08)', padding: 4 }} data-testid="detail-tab-bar">
          {tabs.map(t => (
            <button key={t.key} onClick={() => setTab(t.key)} style={{ flex: 1, padding: '10px 12px', borderRadius: 8, border: 'none', background: tab === t.key ? '#0A0F2E' : 'transparent', color: tab === t.key ? '#fff' : 'rgba(10,10,26,0.5)', fontSize: 12, fontWeight: 600, cursor: 'pointer', fontFamily: "'DM Sans', sans-serif", transition: 'all 0.15s' }} data-testid={`tab-${t.key}`}>{t.label}</button>
          ))}
        </div>

        {/* Failure banner — sticky at the top of every tab if audit failed.
            Forces visibility of the error without requiring a click into Logs. */}
        {audit.status === 'failed' && (
          <div style={{ ...card, background: '#FEF2F2', border: '1px solid #FECACA', marginBottom: 18, padding: '14px 18px' }} data-testid="failure-banner">
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6 }}>
              <AlertTriangle size={18} color="#DC2626" />
              <div style={{ fontWeight: 700, color: '#991B1B', fontSize: 14 }}>Audit failed</div>
            </div>
            <div style={{ fontSize: 13, color: '#7F1D1D', fontFamily: "'SF Mono', 'Fira Code', monospace", wordBreak: 'break-word' }}>
              {audit.error || 'No error message recorded'}
            </div>
            <div style={{ fontSize: 11, color: 'rgba(127,29,29,0.7)', marginTop: 8 }}>
              Open the <strong>Logs</strong> tab for full traceback + run logs.
            </div>
          </div>
        )}

        {/* Tab Content */}
        {tab === 'overview' && <AIOverviewTab ps={ps} card={card} />}
        {tab === 'engines' && <RevenueEnginesTab engines={engines} card={card} currency={audit.currency} report={fr} />}
        {tab === 'workers' && <SiteAuditTab workers={workers} workerSeverity={fr.worker_severity || {}} card={card} />}
        {tab === 'action' && <ActionPlanTab findings={findings} card={card} />}
        {tab === 'history' && <HistoryTab history={history} loading={historyLoading} currentAuditId={id} navigate={navigate} card={card} />}
        {tab === 'raw' && <RawDataTab fr={fr} intake={audit.intake_snapshot} card={card} />}
        {tab === 'logs' && <LogsTab audit={audit} card={card} />}
        {tab === 'email' && <EmailLogTab audit={audit} onResend={handleResend} resending={resending} card={card} />}
        {tab === 'pipeline' && <PipelineTab audit={audit} card={card} />}
      </div>
    </div>
  );
}

// ── Tab Components ──────────────────────────────────────────────────────────

function AIOverviewTab({ ps, card }) {
  const platforms = Object.entries(PLATFORM_LABELS);
  return (
    <div data-testid="tab-content-overview">
      {/* Platform Cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 14, marginBottom: 18 }}>
        {platforms.map(([key, label]) => {
          const p = ps[key] || {};
          const s = p.composite_score || 0;
          return (
            <div key={key} style={{ ...card, textAlign: 'center' }}>
              <img src={PLATFORM_LOGOS[key]} alt={label} width={32} height={32} style={{ margin: '0 auto 8px', display: 'block', borderRadius: 6 }} />
              <div style={{ fontSize: 12, color: 'rgba(10,10,26,0.4)', marginBottom: 4 }}>{label}</div>
              <div style={{ fontSize: 28, fontWeight: 800, color: scoreColor(s), fontFamily: "'Outfit', sans-serif" }}>{s}<span style={{ fontSize: 12, color: 'rgba(10,10,26,0.3)', fontWeight: 400 }}>/100</span></div>
              <div style={{ height: 4, background: '#F5F6FA', borderRadius: 2, marginTop: 8, overflow: 'hidden' }}>
                <div style={{ width: `${s}%`, height: '100%', background: scoreColor(s), borderRadius: 2 }} />
              </div>
              {p.verdict && <div style={{ fontSize: 11, color: 'rgba(10,10,26,0.4)', marginTop: 8 }}>{p.verdict}</div>}
              {p.mentioned_count != null && <div style={{ fontSize: 10, color: 'rgba(10,10,26,0.3)', marginTop: 2 }}>Mentioned: {p.mentioned_count}/{p.total_prompts || '-'}</div>}
            </div>
          );
        })}
      </div>

      {/* Diagnostic Table */}
      <div style={{ ...card, padding: 0, overflow: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
          <thead>
            <tr style={{ background: '#F8F9FF', borderBottom: '1px solid rgba(10,10,26,0.08)' }}>
              <th style={{ padding: '10px 14px', textAlign: 'left', fontSize: 10, fontWeight: 700, textTransform: 'uppercase', color: 'rgba(10,10,26,0.4)' }}>Metric</th>
              {platforms.map(([key, label]) => <th key={key} style={{ padding: '10px 14px', textAlign: 'center', fontSize: 10, fontWeight: 700, textTransform: 'uppercase', color: 'rgba(10,10,26,0.4)' }}>{label}</th>)}
            </tr>
          </thead>
          <tbody>
            {DIAG_KEYS.map(dk => (
              <tr key={dk} style={{ borderBottom: '1px solid rgba(10,10,26,0.05)' }}>
                <td style={{ padding: '10px 14px', fontWeight: 600, color: '#0A0A1A' }}>{DIAG_LABELS[dk]}</td>
                {platforms.map(([key]) => {
                  const val = (ps[key] || {})[dk] || 0;
                  return (
                    <td key={key} style={{ padding: '10px 14px', textAlign: 'center' }}>
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}>
                        <div style={{ width: 40, height: 4, background: '#F5F6FA', borderRadius: 2, overflow: 'hidden' }}>
                          <div style={{ width: `${val * 10}%`, height: '100%', background: scoreColor(val * 10), borderRadius: 2 }} />
                        </div>
                        <span style={{ fontWeight: 700, color: scoreColor(val * 10), fontSize: 12 }}>{val}/10</span>
                      </div>
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

const FOUR_ENGINES = [
  { key: 'rooms',    label: 'Rooms & Suites' },
  { key: 'dining',   label: 'Dining' },
  { key: 'weddings', label: 'Weddings & Events' },
  { key: 'spa',      label: 'Spa & Wellness' },
];

const ENGINE_ICONS = {
  rooms: <svg width="22" height="22" viewBox="0 0 28 28" fill="none"><rect x="2" y="14" width="24" height="9" rx="1" stroke="#1C2470" strokeWidth="1.4"/><rect x="6" y="8" width="8" height="6" rx="1" stroke="#1C2470" strokeWidth="1.4"/><rect x="14" y="8" width="8" height="6" rx="1" stroke="#1C2470" strokeWidth="1.4"/><line x1="2" y1="23" x2="2" y2="26" stroke="#1C2470" strokeWidth="1.4" strokeLinecap="round"/><line x1="26" y1="23" x2="26" y2="26" stroke="#1C2470" strokeWidth="1.4" strokeLinecap="round"/></svg>,
  dining: <svg width="22" height="22" viewBox="0 0 28 28" fill="none"><path d="M4 18 Q4 8 14 8 Q24 8 24 18Z" stroke="#1C2470" strokeWidth="1.4" strokeLinejoin="round"/><line x1="2" y1="18" x2="26" y2="18" stroke="#1C2470" strokeWidth="1.4" strokeLinecap="round"/><line x1="14" y1="5" x2="14" y2="8" stroke="#1C2470" strokeWidth="1.4" strokeLinecap="round"/><circle cx="14" cy="4.5" r="1.5" stroke="#1C2470" strokeWidth="1.2"/></svg>,
  weddings: <svg width="22" height="22" viewBox="0 0 28 28" fill="none"><circle cx="10" cy="14" r="7" stroke="#1C2470" strokeWidth="1.4"/><circle cx="18" cy="14" r="7" stroke="#1C2470" strokeWidth="1.4"/></svg>,
  spa: <svg width="22" height="22" viewBox="0 0 28 28" fill="none"><path d="M14 4 C14 4 6 13 6 18 A8 8 0 0 0 22 18 C22 13 14 4 14 4Z" stroke="#1C2470" strokeWidth="1.4" strokeLinejoin="round" opacity="0.3"/><path d="M10 19 Q11 16 14 15" stroke="#1C2470" strokeWidth="1.2" strokeLinecap="round" opacity="0.2"/></svg>,
};

function buildAssetRowsV2(engineKey, engineData, assetInventory, schemaSubScores) {
  const ENGINE_SCHEMA_KEYS = {
    rooms: 'room_inventory', dining: 'dining_service',
    weddings: 'faq_conversational', spa: 'dining_service',
  };
  const ENGINE_GAP_TEXT = {
    rooms:    (name) => `No HotelRoom schema — AI cannot recommend ${name} by name`,
    dining:   () => `No FoodEstablishment schema — no menu or cuisine details`,
    weddings: () => `No venue schema — capacity and package details not AI-readable`,
    spa:      () => `No spa schema — treatments and facilities invisible to AI`,
  };
  const ENGINE_PRIORITY_FIX = {
    rooms: 'Add HotelRoom schema for each named room type and suite',
    dining: 'Add FoodEstablishment schema to each restaurant + create menu page',
    weddings: 'Add venue capacity, ceremony types, pricing and FAQPage schema',
    spa: 'Create dedicated Spa page with HealthAndBeautyBusiness schema',
  };

  const namedAssets = assetInventory?.[engineKey] || [];
  const uniqueAssets = assetInventory?.unique_assets || [];
  const topCompetitors = engineData?.top_competitors || [];
  const schemaKey = ENGINE_SCHEMA_KEYS[engineKey] || 'local_business';
  const schemaScore = schemaSubScores?.[schemaKey] ?? 0;
  const rows = [];

  namedAssets.forEach((assetName, i) => {
    const competitor = topCompetitors[i % Math.max(topCompetitors.length, 1)] || '';
    rows.push({
      asset: assetName,
      status: schemaScore > 0 ? 'Weak' : 'Missing',
      gap: schemaScore > 0
        ? 'Schema present but incomplete — missing key fields for AI citation'
        : (ENGINE_GAP_TEXT[engineKey] || ((n) => `No schema — not visible to AI`))(assetName),
      competitor: competitor
        ? `${competitor} — ${schemaScore > 0 ? 'more complete schema' : 'full schema and dedicated page'}`
        : '',
    });
  });

  if ((engineKey === 'spa' || engineKey === 'weddings') && uniqueAssets.length > 0) {
    uniqueAssets.forEach(name => {
      rows.push({
        asset: name, status: 'Missing',
        gap: 'Unique asset — not visible in AI-readable structured content',
        competitor: 'No direct competitor — first-mover opportunity',
      });
    });
  }

  if (rows.length === 0) {
    const fallbackLabels = { rooms: 'Room & Suite Inventory', dining: 'Restaurant & Dining', weddings: 'Wedding Venue', spa: 'Spa & Wellness Facilities' };
    rows.push({
      asset: fallbackLabels[engineKey] || engineKey,
      status: schemaScore > 0 ? 'Weak' : 'Missing',
      gap: schemaScore > 0
        ? 'Schema present but incomplete — missing key fields for AI citation'
        : 'No schema found — AI cannot identify or recommend this offering',
      competitor: topCompetitors[0] ? `${topCompetitors[0]} — full schema and dedicated pages` : '',
    });
  }

  return { rows, priorityFix: ENGINE_PRIORITY_FIX[engineKey] || '' };
}

function RevenueEnginesTab({ engines, card, currency, report }) {
  const assetInventory = report?.asset_inventory || {};
  const schemaSubScores = report?.worker_details?.schema?.sub_scores || {};
  // Phase 2 backend output. When present, prefer this over the legacy client-
  // side buildAssetRowsV2 reconstruction so the admin UI surfaces the same
  // audit-grounded aspect rows the public HTML report renders.
  const engineAssetAnalysis = report?.engine_asset_analysis || {};

  // Palette per palette_lock memory: monochrome navy/blue only, no red/amber.
  // Aligns with backend report_renderer._severity_badge() exactly.
  const sevBadge = (sev) => {
    const configs = {
      critical:   { bg: '#F0F1F7', color: '#0A0F2E', label: 'Critical' },
      weak:       { bg: '#F0F1F7', color: '#0A0F2E', label: 'Weak' },
      needs_work: { bg: '#F0F1F7', color: '#0A0F2E', label: 'Needs Work' },
      strong:     { bg: '#EBF0FF', color: '#2D4DE0', label: 'Good' },
      good:       { bg: '#EBF0FF', color: '#2D4DE0', label: 'Good' },
    };
    const c = configs[sev] || configs.critical;
    return <span style={{ fontSize: 9, fontWeight: 700, padding: '2px 0', borderRadius: 20, background: c.bg, color: c.color, width: 56, textAlign: 'center', display: 'inline-block', whiteSpace: 'nowrap' }}>{c.label}</span>;
  };
  // Row-level status — same palette as sevBadge so the UI reads as one system.
  // Accepts both new backend values ("critical/weak/needs_work/strong") and
  // the legacy capitalised values ("Missing/Weak/Present") for back-compat
  // with old DB audit payloads.
  const statusBadge = (status) => {
    const legacyMap = { Missing: 'critical', Weak: 'weak', Present: 'strong', Good: 'strong' };
    const normalised = legacyMap[status] || (status || '').toLowerCase();
    return sevBadge(normalised);
  };
  const dotBar = (score) => {
    const filled = score >= 80 ? 5 : score >= 60 ? 4 : score >= 40 ? 3 : score >= 20 ? 2 : 1;
    return (
      <div style={{ display: 'flex', alignItems: 'center', marginTop: 5 }}>
        {[0,1,2,3,4].map(i => <span key={i} style={{ display: 'inline-block', width: 14, height: 3, borderRadius: 2, background: i < filled ? '#2D4DE0' : '#E8E9EF', marginRight: 3 }} />)}
      </div>
    );
  };

  return (
    <div data-testid="tab-content-engines">
      {/* Summary grid */}
      <div style={{ fontSize: 9, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.1em', color: 'rgba(10,10,26,0.35)', marginBottom: 11 }}>Revenue engine scores at a glance</div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 10, marginBottom: 18 }}>
        {FOUR_ENGINES.map(engine => {
          const ed = report?.engine_data?.[engine.key];
          const hasNamedAssets = (assetInventory?.[engine.key] || []).length > 0;
          const hasCaptureRate = ed && ed.capture_rate > 0;
          const isBlurred = !hasNamedAssets && !hasCaptureRate;
          const score = ed ? Math.round(ed.module_score) : 0;
          const severity = ed?.severity || 'critical';
          return (
            <div key={engine.key} data-testid={`engine-card-${engine.key}`} style={{ background: '#fff', border: '1px solid rgba(10,10,26,0.08)', borderRadius: 12, boxShadow: '0 1px 3px rgba(0,0,0,.04)', padding: '14px 16px', opacity: isBlurred ? 0.55 : 1, borderLeft: isBlurred ? '3px solid rgba(10,10,26,0.12)' : '3px solid #2D4DE0' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
                <div style={{ width: 36, height: 36, flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#F5F6FA', borderRadius: 8, border: '1px solid rgba(10,10,26,0.06)' }}>
                  {ENGINE_ICONS[engine.key]}
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 2 }}>
                    <span style={{ fontSize: 9, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.1em', color: isBlurred ? 'rgba(10,10,26,0.3)' : 'rgba(10,10,26,0.35)' }}>{engine.label}</span>
                    {sevBadge(severity)}
                  </div>
                  <div style={{ fontFamily: "'Outfit', sans-serif", fontSize: 22, fontWeight: 800, color: '#0A0F2E', lineHeight: 1, marginBottom: 2 }}>{score}<span style={{ fontSize: 12, fontWeight: 500, color: 'rgba(10,10,26,0.25)' }}>/100</span></div>
                  {dotBar(score)}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Asset visibility tables */}
      <div style={{ fontSize: 9, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.1em', color: 'rgba(10,10,26,0.35)', marginBottom: 11 }}>Asset visibility by revenue engine</div>
      {/* Phase 2 source-of-truth disclosure — single global statement so each
          engine card's priority_fix bar doesn't need to repeat the disclaimer. */}
      <div style={{ background: '#F5F6FA', border: '1px solid rgba(10,10,26,0.06)', borderRadius: 10, padding: '10px 14px', marginBottom: 14, display: 'flex', alignItems: 'center', gap: 10 }}>
        <span style={{ fontSize: 9, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.09em', color: 'rgba(10,10,26,0.3)', flexShrink: 0 }}>Source of evidence</span>
        <span style={{ fontSize: 11, color: 'rgba(10,10,26,0.65)', lineHeight: 1.5 }}>
          All gaps and actions below are detected directly from this property's published content — scraped subpage text, asset inventory extracted from that text, and the AI-probe response data captured during this audit. No claims about competitors or industry benchmarks are inferred or fabricated.
        </span>
      </div>
      {FOUR_ENGINES.map(engine => {
        const ed = report?.engine_data?.[engine.key] || {};
        const hasNamedAssets = (assetInventory?.[engine.key] || []).length > 0;
        const hasCaptureRate = ed.capture_rate > 0;
        const isBlurred = !hasNamedAssets && !hasCaptureRate;
        const schemaKey = ({ rooms: 'room_inventory', dining: 'dining_service', weddings: 'faq_conversational', spa: 'dining_service' })[engine.key] || 'local_business';
        const schemaScore = schemaSubScores?.[schemaKey] ?? 0;
        const isQuickWin = ed.capture_rate > 0 && schemaScore === 0;
        // Prefer Phase 2 backend output when present. The frontend FOUR_ENGINES
        // uses "weddings" as the meetings/events engine key; backend produces
        // "meetings" — accept either so old and new payloads both render.
        const backendBlock =
          engineAssetAnalysis?.[engine.key] ||
          (engine.key === 'weddings' ? engineAssetAnalysis?.meetings : null);
        let rows, priorityFix;
        if (backendBlock?.assets && Array.isArray(backendBlock.assets)) {
          rows = backendBlock.assets.map(a => ({
            asset:      a.name,
            status:     a.status,
            gap:        a.gap,
            competitor: a.competitor_action,  // backend keeps field name for back-compat; renders under "Action required" column
          }));
          priorityFix = backendBlock.priority_fix || '';
        } else {
          const v2 = buildAssetRowsV2(engine.key, ed, assetInventory, schemaSubScores);
          rows = v2.rows;
          priorityFix = v2.priorityFix;
        }
        const severity = ed?.severity || 'critical';

        return (
          <div key={engine.key} data-testid={`engine-table-${engine.key}`} style={{ background: '#fff', border: '1px solid rgba(10,10,26,0.08)', borderRadius: 14, boxShadow: '0 1px 3px rgba(0,0,0,.04)', marginBottom: 14, overflow: 'hidden', opacity: isBlurred ? 0.55 : 1, borderLeft: isBlurred ? '3px solid rgba(10,10,26,0.12)' : '3px solid #2D4DE0' }}>
            {/* Header */}
            <div style={{ padding: '14px 22px 10px', display: 'flex', alignItems: 'center', gap: 8 }}>
              <span style={{ fontFamily: "'Outfit', sans-serif", fontSize: 14, fontWeight: 800, color: isBlurred ? 'rgba(10,10,26,0.3)' : '#0A0F2E' }}>{engine.label}</span>
              {isQuickWin && <span style={{ fontSize: 9, fontWeight: 700, padding: '2px 8px', borderRadius: 20, background: '#EBF0FF', color: '#2D4DE0' }}>Quick win</span>}
              {sevBadge(severity)}
            </div>
            {/* Table */}
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
              <thead>
                <tr style={{ background: '#F8F9FF', borderTop: '1px solid rgba(10,10,26,0.06)', borderBottom: '1px solid rgba(10,10,26,0.06)' }}>
                  <th style={{ padding: '8px 14px', textAlign: 'left', fontSize: 9, fontWeight: 700, textTransform: 'uppercase', color: 'rgba(10,10,26,0.35)', letterSpacing: '.05em', width: '20%' }}>Asset</th>
                  <th style={{ padding: '8px 14px', textAlign: 'left', fontSize: 9, fontWeight: 700, textTransform: 'uppercase', color: 'rgba(10,10,26,0.35)', letterSpacing: '.05em', width: '10%' }}>Status</th>
                  <th style={{ padding: '8px 14px', textAlign: 'left', fontSize: 9, fontWeight: 700, textTransform: 'uppercase', color: 'rgba(10,10,26,0.35)', letterSpacing: '.05em', width: '37%' }}>Gap</th>
                  <th style={{ padding: '8px 14px', textAlign: 'left', fontSize: 9, fontWeight: 700, textTransform: 'uppercase', color: 'rgba(10,10,26,0.35)', letterSpacing: '.05em', width: '33%' }}>Action required</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row, i) => (
                  <tr key={i} style={{ background: i % 2 === 1 ? '#F9FAFB' : '#fff', borderBottom: '1px solid rgba(10,10,26,0.04)' }}>
                    <td style={{ padding: '10px 14px', fontWeight: 600, color: '#0A0F2E' }}>{row.asset}</td>
                    <td style={{ padding: '10px 14px' }}>{statusBadge(row.status)}</td>
                    <td style={{ padding: '10px 14px', color: 'rgba(10,10,26,0.52)', fontSize: 11 }}>{row.gap}</td>
                    <td style={{ padding: '10px 14px', color: 'rgba(10,10,26,0.45)', fontSize: 11 }}>{row.competitor || '\u2014'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            {/* Priority fix footer */}
            <div style={{ padding: '10px 22px', background: '#F5F6FA', borderTop: '1px solid rgba(10,10,26,0.06)', display: 'flex', alignItems: 'center', gap: 16 }}>
              <span style={{ fontSize: 9, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.09em', color: 'rgba(10,10,26,0.3)', flexShrink: 0 }}>Priority fix</span>
              <span style={{ fontSize: 11, fontWeight: 700, color: isBlurred ? 'rgba(10,10,26,0.3)' : '#0A0F2E' }}>{priorityFix}</span>
            </div>
          </div>
        );
      })}
    </div>
  );
}

// Updated 2026-06-09 to match the redesigned report card order (PR #20).
// Legacy `pagespeed` / `onpage` kept at the tail so admins can still inspect
// historical audits where the merged page_performance key wasn't computed.
const WORKER_DISPLAY_ORDER = [
  'technical', 'page_performance', 'schema', 'content', 'authority',
  'site_architecture', 'pagespeed', 'onpage',
];
// Palette aligned with palette_lock memory + RevenueEnginesTab sevBadge.
// Monochrome navy/blue only — no red, amber, or traffic-light colours.
const SEV_STYLES = {
  critical:   { bg: '#F0F1F7', color: '#0A0F2E' },
  weak:       { bg: '#F0F1F7', color: '#0A0F2E' },
  needs_work: { bg: '#F0F1F7', color: '#0A0F2E' },
  strong:     { bg: '#EBF0FF', color: '#2D4DE0' },
};

function SiteAuditTab({ workers, workerSeverity, card }) {
  // Show workers in defined display order, then any extras
  const orderedKeys = WORKER_DISPLAY_ORDER.filter(k => workers[k]);
  const extraKeys = Object.keys(workers).filter(k => !WORKER_DISPLAY_ORDER.includes(k));
  const allKeys = [...orderedKeys, ...extraKeys];

  if (!allKeys.length) return <div style={{ ...card, textAlign: 'center', padding: 40, color: 'rgba(10,10,26,0.4)' }}>No worker data available</div>;
  return (
    <div style={{ ...card, padding: 0, overflow: 'auto' }} data-testid="tab-content-workers">
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
        <thead>
          <tr style={{ background: '#F8F9FF', borderBottom: '1px solid rgba(10,10,26,0.08)' }}>
            {['Worker', 'Score', 'Severity', 'Key Finding'].map((h, i) => (
              <th key={i} style={{ padding: '10px 14px', textAlign: 'left', fontSize: 10, fontWeight: 700, textTransform: 'uppercase', color: 'rgba(10,10,26,0.4)' }}>{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {allKeys.map(key => {
            const w = workers[key] || {};
            const sev = workerSeverity[key] || '';
            const sevStyle = SEV_STYLES[sev] || { bg: '#F8F9FF', color: 'rgba(10,10,26,0.5)' };
            return (
              <tr key={key} style={{ borderBottom: '1px solid rgba(10,10,26,0.05)' }}>
                <td style={{ padding: '12px 14px', fontWeight: 600, color: '#0A0A1A' }}>{WORKER_NAMES[key] || key}</td>
                <td style={{ padding: '12px 14px' }}>
                  <span style={{ fontWeight: 700, color: scoreColor(w.score || 0) }}>{Math.round(w.score || 0)}/100</span>
                </td>
                <td style={{ padding: '12px 14px' }}>
                  <span style={{ padding: '2px 8px', borderRadius: 4, fontSize: 11, fontWeight: 600, background: sevStyle.bg, color: sevStyle.color, textTransform: 'capitalize' }}>{sev || '-'}</span>
                </td>
                <td style={{ padding: '12px 14px', color: 'rgba(10,10,26,0.5)', fontSize: 12, maxWidth: 350 }}>
                  {(w.findings || []).slice(0, 3).map((f, i) => (
                    <div key={i} style={{ marginBottom: 2 }}>&bull; {typeof f === 'string' ? f : (f.issue || f.label || JSON.stringify(f))}</div>
                  ))}
                  {!(w.findings || []).length && <span style={{ color: 'rgba(10,10,26,0.3)' }}>-</span>}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function ActionPlanTab({ findings, card }) {
  const high = findings.filter(f => ['Critical', 'High'].includes(f.priority)).length;
  const med = findings.filter(f => f.priority === 'Medium').length;
  const low = findings.filter(f => f.priority === 'Low').length;

  return (
    <div data-testid="tab-content-action">
      {/* Priority summary */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 14, marginBottom: 18 }}>
        <div style={{ ...card, background: '#FEF2F2', textAlign: 'center' }}>
          <div style={{ fontSize: 28, fontWeight: 800, color: '#DC2626', fontFamily: "'Outfit', sans-serif" }}>{high}</div>
          <div style={{ fontSize: 11, color: 'rgba(10,10,26,0.5)' }}>High Priority</div>
        </div>
        <div style={{ ...card, background: '#FFFBEB', textAlign: 'center' }}>
          <div style={{ fontSize: 28, fontWeight: 800, color: '#D97706', fontFamily: "'Outfit', sans-serif" }}>{med}</div>
          <div style={{ fontSize: 11, color: 'rgba(10,10,26,0.5)' }}>Medium Priority</div>
        </div>
        <div style={{ ...card, background: '#F8F9FF', textAlign: 'center' }}>
          <div style={{ fontSize: 28, fontWeight: 800, color: 'rgba(10,10,26,0.4)', fontFamily: "'Outfit', sans-serif" }}>{low}</div>
          <div style={{ fontSize: 11, color: 'rgba(10,10,26,0.5)' }}>Low Priority</div>
        </div>
      </div>

      {/* Findings table */}
      {findings.length === 0 ? (
        <div style={{ ...card, textAlign: 'center', padding: 40, color: 'rgba(10,10,26,0.4)' }}>No findings available</div>
      ) : (
        <div style={{ ...card, padding: 0, overflow: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr style={{ background: '#F8F9FF', borderBottom: '1px solid rgba(10,10,26,0.08)' }}>
                {['Priority', 'Source', 'Issue', 'Detail'].map((h, i) => (
                  <th key={i} style={{ padding: '10px 14px', textAlign: 'left', fontSize: 10, fontWeight: 700, textTransform: 'uppercase', color: 'rgba(10,10,26,0.4)' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {findings.map((f, i) => (
                <tr key={i} style={{ borderBottom: '1px solid rgba(10,10,26,0.05)' }}>
                  <td style={{ padding: '12px 14px' }}>
                    <span style={{ padding: '3px 10px', borderRadius: 4, fontSize: 11, fontWeight: 700, color: PRIORITY_COLORS[f.priority] || '#0A0A1A' }}>{f.priority || '-'}</span>
                  </td>
                  <td style={{ padding: '12px 14px', color: 'rgba(10,10,26,0.5)', fontSize: 12 }}>{WORKER_NAMES[f.source_worker] || f.source_worker || '-'}</td>
                  <td style={{ padding: '12px 14px', fontWeight: 600, color: '#0A0A1A' }}>{f.issue || '-'}</td>
                  <td style={{ padding: '12px 14px', color: 'rgba(10,10,26,0.5)', fontSize: 12, maxWidth: 350 }}>{f.detail || '-'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function HistoryTab({ history, loading, currentAuditId, navigate, card }) {
  if (loading) return <div style={card}><div style={{ color: 'rgba(10,10,26,0.5)', padding: '20px' }}>Loading history…</div></div>;
  if (!history) return <div style={card}><div style={{ color: 'rgba(10,10,26,0.5)', padding: '20px' }}>Open this tab to load history.</div></div>;
  if (history.error) return <div style={card}><div style={{ color: '#DC2626', padding: '20px' }}>Failed to load history.</div></div>;
  const runs = history.runs || [];
  if (runs.length <= 1) {
    return (
      <div style={card}>
        <div style={{ fontFamily: "'Outfit', sans-serif", fontSize: 15, fontWeight: 700, marginBottom: 6 }}>First audit for this property</div>
        <div style={{ fontSize: 12, color: 'rgba(10,10,26,0.5)' }}>
          No prior audits found at <strong style={{ color: '#0A0A1A' }}>{history.target_host || history.source_url}</strong>.
          Run the audit again to build a score timeline and track improvements.
        </div>
      </div>
    );
  }

  // Reverse chronological for display — newest first reads naturally in a list
  const runsDesc = [...runs].reverse();

  // Find the previous completed run (first run before this one that has final_score)
  const currentIdx = runs.findIndex(r => r._id === currentAuditId || r.audit_id === currentAuditId);
  const prevRun = currentIdx > 0
    ? [...runs.slice(0, currentIdx)].reverse().find(r => typeof r.final_score === 'number')
    : null;
  const currentRun = currentIdx >= 0 ? runs[currentIdx] : runs[runs.length - 1];

  const delta = (now, then) => {
    if (typeof now !== 'number' || typeof then !== 'number') return null;
    return Math.round((now - then) * 10) / 10;
  };
  const DeltaBadge = ({ value }) => {
    if (value === null || value === undefined) return null;
    if (value === 0) return <span style={{ fontSize: 11, color: 'rgba(10,10,26,0.35)', marginLeft: 6 }}>±0</span>;
    const up = value > 0;
    return <span style={{ fontSize: 11, fontWeight: 700, color: up ? '#22c55e' : '#DC2626', marginLeft: 6 }}>{up ? '▲' : '▼'}{Math.abs(value)}</span>;
  };

  // Updated 2026-06-09: include the new page_performance + site_architecture
  // keys for current audits. Legacy pagespeed + onpage kept at the tail so
  // the "vs previous run" diff still renders meaningfully when an older
  // audit had them computed separately.
  const worker_keys = [
    'schema', 'content', 'authority', 'technical',
    'page_performance', 'site_architecture',
    'pagespeed', 'onpage',
  ];
  const WORKER_LABEL = {
    schema: 'Schema', content: 'Content', authority: 'Authority',
    technical: 'AI Access', page_performance: 'Page Perf', site_architecture: 'Site Arch',
    pagespeed: 'PageSpeed', onpage: 'On-Page',
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      {/* Change summary (only if we have a previous run) */}
      {prevRun && currentRun && (
        <div style={card}>
          <div style={{ fontFamily: "'Outfit', sans-serif", fontSize: 15, fontWeight: 700, marginBottom: 4 }}>Change vs previous audit</div>
          <div style={{ fontSize: 11, color: 'rgba(10,10,26,0.5)', marginBottom: 14 }}>
            Comparing this run to audit from <strong>{fmtDate(prevRun.created_at)}</strong>.
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 12, marginBottom: 14 }}>
            <DeltaTile label="Final Score" now={currentRun.final_score} prev={prevRun.final_score} deltaFn={delta} />
            <DeltaTile label="AI Visibility" now={currentRun.ai_visibility_score} prev={prevRun.ai_visibility_score} deltaFn={delta} />
            <DeltaTile label="Revenue at Risk" now={currentRun.room_revenue_at_risk} prev={prevRun.room_revenue_at_risk} deltaFn={delta} format={fmtNum} invertColor />
            <DeltaTile label="Duration (s)" now={currentRun.duration_s} prev={prevRun.duration_s} deltaFn={delta} invertColor />
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(6, 1fr)', gap: 10, marginBottom: 6 }}>
            {worker_keys.map(k => (
              <DeltaTile key={k} label={WORKER_LABEL[k]} now={currentRun.worker_scores?.[k]} prev={prevRun.worker_scores?.[k]} deltaFn={delta} small />
            ))}
          </div>
        </div>
      )}

      {/* Full timeline */}
      <div style={card}>
        <div style={{ fontFamily: "'Outfit', sans-serif", fontSize: 15, fontWeight: 700, marginBottom: 4 }}>Audit timeline ({runs.length} runs)</div>
        <div style={{ fontSize: 11, color: 'rgba(10,10,26,0.5)', marginBottom: 14 }}>
          All audits for <strong style={{ color: '#0A0A1A' }}>{history.target_host}</strong>, newest first. Click any row to open that audit.
        </div>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
          <thead>
            <tr style={{ borderBottom: '1px solid rgba(10,10,26,0.08)' }}>
              <th style={{ textAlign: 'left', padding: '8px 10px', fontSize: 10, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'rgba(10,10,26,0.4)' }}>When</th>
              <th style={{ textAlign: 'left', padding: '8px 10px', fontSize: 10, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'rgba(10,10,26,0.4)' }}>Score</th>
              <th style={{ textAlign: 'left', padding: '8px 10px', fontSize: 10, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'rgba(10,10,26,0.4)' }}>Grade</th>
              <th style={{ textAlign: 'left', padding: '8px 10px', fontSize: 10, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'rgba(10,10,26,0.4)' }}>AI Vis</th>
              <th style={{ textAlign: 'left', padding: '8px 10px', fontSize: 10, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'rgba(10,10,26,0.4)' }}>ChatGPT</th>
              <th style={{ textAlign: 'left', padding: '8px 10px', fontSize: 10, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'rgba(10,10,26,0.4)' }}>Gemini</th>
              <th style={{ textAlign: 'left', padding: '8px 10px', fontSize: 10, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'rgba(10,10,26,0.4)' }}>Perplexity</th>
              <th style={{ textAlign: 'left', padding: '8px 10px', fontSize: 10, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'rgba(10,10,26,0.4)' }}>Claude</th>
              <th style={{ textAlign: 'right', padding: '8px 10px', fontSize: 10, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'rgba(10,10,26,0.4)' }}>Rev-at-Risk</th>
            </tr>
          </thead>
          <tbody>
            {runsDesc.map((r, i) => {
              const isCurrent = r._id === currentAuditId || r.audit_id === currentAuditId;
              return (
                <tr key={r._id || i} onClick={() => !isCurrent && navigate(`/admin/audits/${r._id}`)}
                    style={{ borderBottom: '1px solid rgba(10,10,26,0.04)', cursor: isCurrent ? 'default' : 'pointer', background: isCurrent ? '#EEF2FF' : 'transparent' }}>
                  <td style={{ padding: '10px', color: 'rgba(10,10,26,0.7)' }}>
                    {fmtDate(r.created_at)}
                    {isCurrent && <span style={{ marginLeft: 6, fontSize: 10, fontWeight: 700, color: '#2D4DE0', background: '#DDE3FF', padding: '1px 6px', borderRadius: 10 }}>THIS</span>}
                  </td>
                  <td style={{ padding: '10px' }}>
                    {typeof r.final_score === 'number' ? (
                      <span style={{ fontWeight: 700, color: scoreColor(r.final_score) }}>{Math.round(r.final_score)}/100</span>
                    ) : '—'}
                  </td>
                  <td style={{ padding: '10px' }}>
                    {r.grade ? (
                      <span style={{ display: 'inline-block', padding: '2px 8px', borderRadius: 6, fontSize: 11, fontWeight: 700, color: '#fff', background: gradeColor(r.grade) }}>{r.grade}</span>
                    ) : '—'}
                  </td>
                  <td style={{ padding: '10px', color: 'rgba(10,10,26,0.7)' }}>{typeof r.ai_visibility_score === 'number' ? Math.round(r.ai_visibility_score) : '—'}</td>
                  <td style={{ padding: '10px', color: 'rgba(10,10,26,0.7)' }}>{typeof r.platform_scores?.chatgpt === 'number' ? Math.round(r.platform_scores.chatgpt) : '—'}</td>
                  <td style={{ padding: '10px', color: 'rgba(10,10,26,0.7)' }}>{typeof r.platform_scores?.gemini === 'number' ? Math.round(r.platform_scores.gemini) : '—'}</td>
                  <td style={{ padding: '10px', color: 'rgba(10,10,26,0.7)' }}>{typeof r.platform_scores?.perplexity === 'number' ? Math.round(r.platform_scores.perplexity) : '—'}</td>
                  <td style={{ padding: '10px', color: 'rgba(10,10,26,0.7)' }}>{typeof r.platform_scores?.claude === 'number' ? Math.round(r.platform_scores.claude) : '—'}</td>
                  <td style={{ padding: '10px', textAlign: 'right', color: 'rgba(10,10,26,0.7)' }}>{typeof r.room_revenue_at_risk === 'number' ? fmtNum(r.room_revenue_at_risk) : '—'}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function DeltaTile({ label, now, prev, deltaFn, format, small, invertColor }) {
  const d = deltaFn(now, prev);
  const fmt = format || ((v) => typeof v === 'number' ? Math.round(v) : '—');
  const isImprovement = invertColor ? (d !== null && d < 0) : (d !== null && d > 0);
  const isRegression  = invertColor ? (d !== null && d > 0) : (d !== null && d < 0);
  const deltaColor = d === null ? 'rgba(10,10,26,0.35)' : isImprovement ? '#22c55e' : isRegression ? '#DC2626' : 'rgba(10,10,26,0.4)';
  return (
    <div style={{ background: '#F8F9FF', borderRadius: 8, padding: small ? '8px 10px' : '12px 14px', border: '1px solid rgba(10,10,26,0.06)' }}>
      <div style={{ fontSize: 9, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'rgba(10,10,26,0.4)', marginBottom: 4 }}>{label}</div>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 4 }}>
        <span style={{ fontFamily: "'Outfit', sans-serif", fontSize: small ? 16 : 20, fontWeight: 800, color: '#0A0A1A' }}>{fmt(now)}</span>
        {d !== null && (
          <span style={{ fontSize: small ? 10 : 11, fontWeight: 700, color: deltaColor }}>
            {d > 0 ? '+' : ''}{d}
          </span>
        )}
      </div>
      <div style={{ fontSize: 9, color: 'rgba(10,10,26,0.35)', marginTop: 2 }}>
        was {fmt(prev)}
      </div>
    </div>
  );
}

function RawDataTab({ fr, intake, card }) {
  const [copied, setCopied] = useState(false);
  // For failed audits there's no full_report — fall back to intake snapshot
  // so admins still see *something* to debug (the form payload that triggered
  // the run).
  const hasReport = fr && Object.keys(fr).length > 0;
  const display = hasReport ? fr : (intake || { _note: 'No report or intake snapshot recorded.' });
  const json = JSON.stringify(display, null, 2);
  const failedWorkers = fr.workers_failed || [];

  const handleCopy = () => {
    navigator.clipboard.writeText(json).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  return (
    <div data-testid="tab-content-raw">
      {!hasReport && intake && (
        <div style={{ ...card, background: '#FFFBEB', border: '1px solid #FDE68A', marginBottom: 14, display: 'flex', alignItems: 'center', gap: 10 }}>
          <AlertTriangle size={18} color="#92400E" />
          <div>
            <div style={{ fontWeight: 700, color: '#92400E', fontSize: 13 }}>No report — showing intake snapshot</div>
            <div style={{ fontSize: 12, color: '#92400E' }}>The audit failed before a report was generated; this is the form payload it received.</div>
          </div>
        </div>
      )}
      {failedWorkers.length > 0 && (
        <div style={{ ...card, background: '#FEF2F2', border: '1px solid #FECACA', marginBottom: 14, display: 'flex', alignItems: 'center', gap: 10 }}>
          <AlertTriangle size={18} color="#DC2626" />
          <div>
            <div style={{ fontWeight: 700, color: '#DC2626', fontSize: 13 }}>Workers Failed</div>
            <div style={{ fontSize: 12, color: '#DC2626' }}>{failedWorkers.join(', ')}</div>
          </div>
        </div>
      )}
      <div style={{ ...card, position: 'relative' }}>
        <button onClick={handleCopy} style={{ position: 'absolute', top: 14, right: 14, padding: '6px 12px', border: '1px solid rgba(10,10,26,0.14)', borderRadius: 6, background: '#fff', cursor: 'pointer', fontSize: 12, fontWeight: 600, color: 'rgba(10,10,26,0.52)', display: 'flex', alignItems: 'center', gap: 4, fontFamily: "'DM Sans', sans-serif" }} data-testid="btn-copy-json">
          {copied ? <><Check size={12} /> Copied</> : <><Copy size={12} /> Copy</>}
        </button>
        <pre style={{ margin: 0, fontSize: 11, lineHeight: 1.5, color: '#0A0A1A', overflow: 'auto', maxHeight: 600, whiteSpace: 'pre-wrap', wordBreak: 'break-all', fontFamily: "'SF Mono', 'Fira Code', monospace" }}>{json}</pre>
      </div>
    </div>
  );
}

function LogsTab({ audit, card }) {
  const [copied, setCopied] = useState(false);
  const logs = audit.logs || [];
  const trace = audit.traceback || '';
  const error = audit.error || '';
  const isFailed = audit.status === 'failed';

  const allText = [
    error && `=== ERROR ===\n${error}`,
    trace && `=== TRACEBACK ===\n${trace}`,
    logs.length && `=== LOGS (${logs.length} lines) ===\n${logs.join('\n')}`,
  ].filter(Boolean).join('\n\n');

  const handleCopy = () => {
    navigator.clipboard.writeText(allText).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  if (!error && !trace && !logs.length) {
    return (
      <div style={{ ...card, padding: 32, textAlign: 'center', color: 'rgba(10,10,26,0.4)' }} data-testid="tab-content-logs">
        No logs captured for this audit yet.
        {audit.status === 'queued' && ' Audit is queued — logs appear once it starts running.'}
        {audit.status === 'processing' && ' Audit is in progress — refresh once it finishes.'}
      </div>
    );
  }

  return (
    <div data-testid="tab-content-logs">
      {isFailed && error && (
        <div style={{ ...card, background: '#FEF2F2', border: '1px solid #FECACA', marginBottom: 14 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
            <AlertTriangle size={16} color="#DC2626" />
            <div style={{ fontWeight: 700, color: '#991B1B', fontSize: 13 }}>Error</div>
          </div>
          <pre style={{ margin: 0, fontSize: 12, color: '#7F1D1D', fontFamily: "'SF Mono', 'Fira Code', monospace", whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{error}</pre>
        </div>
      )}

      {trace && (
        <div style={{ ...card, marginBottom: 14, position: 'relative' }}>
          <div style={{ fontWeight: 700, fontSize: 13, color: '#0A0A1A', marginBottom: 8 }}>Traceback</div>
          <pre style={{ margin: 0, fontSize: 11, lineHeight: 1.5, color: '#7F1D1D', overflow: 'auto', maxHeight: 320, whiteSpace: 'pre-wrap', wordBreak: 'break-all', fontFamily: "'SF Mono', 'Fira Code', monospace", background: '#FEF7F7', padding: 10, borderRadius: 6 }}>{trace}</pre>
        </div>
      )}

      <div style={{ ...card, position: 'relative' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
          <div style={{ fontWeight: 700, fontSize: 13, color: '#0A0A1A' }}>Run logs ({logs.length} lines)</div>
          <button onClick={handleCopy} style={{ padding: '6px 12px', border: '1px solid rgba(10,10,26,0.14)', borderRadius: 6, background: '#fff', cursor: 'pointer', fontSize: 12, fontWeight: 600, color: 'rgba(10,10,26,0.52)', display: 'flex', alignItems: 'center', gap: 4, fontFamily: "'DM Sans', sans-serif" }} data-testid="btn-copy-logs">
            {copied ? <><Check size={12} /> Copied all</> : <><Copy size={12} /> Copy all</>}
          </button>
        </div>
        {logs.length > 0 ? (
          <pre style={{ margin: 0, fontSize: 11, lineHeight: 1.5, color: '#0A0A1A', overflow: 'auto', maxHeight: 600, whiteSpace: 'pre-wrap', wordBreak: 'break-all', fontFamily: "'SF Mono', 'Fira Code', monospace", background: '#0F172A', color: '#E2E8F0', padding: 12, borderRadius: 6 }}>{logs.join('\n')}</pre>
        ) : (
          <div style={{ color: 'rgba(10,10,26,0.4)', fontSize: 12 }}>No log lines captured.</div>
        )}
      </div>
    </div>
  );
}

function EmailLogTab({ audit, onResend, resending, card }) {
  const emailLog = audit.email_log || [];
  return (
    <div data-testid="tab-content-email">
      <div style={{ ...card, marginBottom: 14, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <div>
          <div style={{ fontWeight: 700, fontSize: 14, color: '#0A0A1A' }}>Email History</div>
          <div style={{ fontSize: 12, color: 'rgba(10,10,26,0.4)', marginTop: 2 }}>Recipient: {audit.email || 'No email on record'}</div>
        </div>
        <button onClick={onResend} disabled={resending || !audit.email} style={{ padding: '7px 16px', border: 'none', borderRadius: 8, background: '#2D4DE0', color: '#fff', fontSize: 12, fontWeight: 700, cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 6, opacity: (!audit.email || resending) ? 0.5 : 1, fontFamily: "'DM Sans', sans-serif" }} data-testid="btn-resend-tab">
          <Mail size={14} /> {resending ? 'Sending...' : 'Resend Email'}
        </button>
      </div>

      {emailLog.length === 0 ? (
        <div style={{ ...card, textAlign: 'center', padding: 48 }}>
          <Mail size={36} color="rgba(10,10,26,0.15)" style={{ margin: '0 auto 12px', display: 'block' }} />
          <div style={{ fontSize: 14, fontWeight: 600, color: 'rgba(10,10,26,0.4)', marginBottom: 4 }}>No email history</div>
          <div style={{ fontSize: 12, color: 'rgba(10,10,26,0.3)' }}>Click "Resend Email" above to send the report.</div>
        </div>
      ) : (
        <div style={{ ...card, padding: 0, overflow: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr style={{ background: '#F8F9FF', borderBottom: '1px solid rgba(10,10,26,0.08)' }}>
                {['Date', 'Recipient', 'Status', 'Provider'].map((h, i) => (
                  <th key={i} style={{ padding: '10px 14px', textAlign: 'left', fontSize: 10, fontWeight: 700, textTransform: 'uppercase', color: 'rgba(10,10,26,0.4)' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {emailLog.map((e, i) => (
                <tr key={i} style={{ borderBottom: '1px solid rgba(10,10,26,0.05)' }}>
                  <td style={{ padding: '12px 14px' }}>{fmtDate(e.sent_at)}</td>
                  <td style={{ padding: '12px 14px' }}>{e.recipient}</td>
                  <td style={{ padding: '12px 14px' }}>
                    <span style={{ padding: '2px 8px', borderRadius: 4, fontSize: 11, fontWeight: 600, background: e.status === 'sent' ? '#ECFDF5' : '#FEF2F2', color: e.status === 'sent' ? '#22c55e' : '#DC2626' }}>{e.status}</span>
                  </td>
                  <td style={{ padding: '12px 14px', color: 'rgba(10,10,26,0.5)' }}>{e.provider || 'MS Graph'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}


function PipelineTab({ audit, card }) {
  const timings = audit.step_timings || {};
  const probe = audit.probe_summary || {};
  const dur = audit.duration_s;

  const steps = [
    { label: 'Hotel Profile',        key: 'profile_s',    desc: 'Claude classifies archetype and modules' },
    { label: 'Workers (parallel)',   key: 'workers_s',    desc: 'crawl, pagespeed, content, schema, geo' },
    { label: 'LLM Probe',           key: 'probe_s',      desc: `${probe.total_calls || 0} calls across 4 platforms` },
    { label: 'Aggregation + Render', key: 'aggregation_s',desc: 'Score calculation and HTML report' },
  ];

  return (
    <div data-testid="tab-content-pipeline">
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 14, marginBottom: 18 }}>
        <div style={{ ...card, textAlign: 'center' }}>
          <div style={{ fontSize: 32, fontWeight: 800, fontFamily: "'Outfit', sans-serif",
            color: (dur||0) > 150 ? '#DC2626' : (dur||0) > 90 ? '#D97706' : '#22c55e' }}>
            {dur ? `${dur}s` : '-'}
          </div>
          <div style={{ fontSize: 11, color: 'rgba(10,10,26,0.4)' }}>Total duration</div>
        </div>
        <div style={{ ...card, textAlign: 'center' }}>
          <div style={{ fontSize: 32, fontWeight: 800, fontFamily: "'Outfit', sans-serif",
            color: (probe.total_timeouts||0) > 0 ? '#DC2626' : '#22c55e' }}>
            {probe.total_timeouts ?? '-'}
          </div>
          <div style={{ fontSize: 11, color: 'rgba(10,10,26,0.4)' }}>Probe timeouts</div>
        </div>
        <div style={{ ...card, textAlign: 'center' }}>
          <div style={{ fontSize: 32, fontWeight: 800, color: '#0A0A1A', fontFamily: "'Outfit', sans-serif" }}>
            {probe.total_calls ?? '-'}
          </div>
          <div style={{ fontSize: 11, color: 'rgba(10,10,26,0.4)' }}>Total API calls</div>
        </div>
      </div>

      <div style={{ ...card, marginBottom: 14 }}>
        <div style={{ fontWeight: 700, fontSize: 13, color: '#0A0A1A', marginBottom: 14 }}>Step Timings</div>
        {steps.map(step => {
          const val = timings[step.key] || 0;
          const pct = dur ? Math.min((val / dur) * 100, 100) : 0;
          const color = val > 60 ? '#DC2626' : val > 30 ? '#D97706' : '#22c55e';
          return (
            <div key={step.key} style={{ marginBottom: 14 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                <div>
                  <span style={{ fontWeight: 600, fontSize: 13, color: '#0A0A1A' }}>{step.label}</span>
                  <span style={{ fontSize: 11, color: 'rgba(10,10,26,0.4)', marginLeft: 8 }}>{step.desc}</span>
                </div>
                <span style={{ fontWeight: 700, color, fontSize: 13 }}>{val ? `${val}s` : '-'}</span>
              </div>
              <div style={{ height: 6, background: '#F5F6FA', borderRadius: 3, overflow: 'hidden' }}>
                <div style={{ width: `${pct}%`, height: '100%', background: color, borderRadius: 3 }} />
              </div>
            </div>
          );
        })}
      </div>

      {(probe.total_calls > 0) && (
        <div style={{ ...card }}>
          <div style={{ fontWeight: 700, fontSize: 13, color: '#0A0A1A', marginBottom: 14 }}>Probe Health by Platform</div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 12 }}>
            {['chatgpt','gemini','perplexity','claude'].map(pk => {
              const timeouts = probe[`${pk}_timeouts`] || 0;
              return (
                <div key={pk} style={{ textAlign: 'center', padding: 12, background: '#F8F9FF', borderRadius: 8 }}>
                  <img src={PLATFORM_LOGOS[pk]} alt={pk} width={24} height={24}
                    style={{ margin: '0 auto 6px', display: 'block', borderRadius: 4 }} />
                  <div style={{ fontSize: 11, color: 'rgba(10,10,26,0.4)', marginBottom: 4 }}>{PLATFORM_LABELS[pk]}</div>
                  <div style={{ fontSize: 18, fontWeight: 800, fontFamily: "'Outfit', sans-serif",
                    color: timeouts > 0 ? '#DC2626' : '#22c55e' }}>
                    {timeouts}
                    <span style={{ fontSize: 11, fontWeight: 400, color: 'rgba(10,10,26,0.3)' }}> timeouts</span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
